import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Search } from 'lucide-react';
import {
  AttendanceApi,
  AttendanceStatus,
  StudentAttendanceRosterRow,
} from '@/services/school.api';
import { AcademicClassesApi } from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input, Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { useTerminology } from '@/hooks/useTerminology';
import { useDefaultYear } from './useDefaultYear';

const STATUS_TONE: Record<AttendanceStatus, 'green' | 'red' | 'amber' | 'blue' | 'slate'> = {
  present: 'green',
  absent: 'red',
  late: 'amber',
  half_day: 'blue',
  holiday: 'slate',
};

const STATUS_LABEL: Record<AttendanceStatus, string> = {
  present: 'Present',
  absent: 'Absent',
  late: 'Late',
  half_day: 'Half Day',
  holiday: 'Holiday',
};

const columnHelper = createColumnHelper<StudentAttendanceRosterRow>();

export function ViewAttendancePage() {
  const term = useTerminology();
  const dt = useDataTable({ defaultLimit: 20, defaultSortBy: 'rollNumber', defaultSortOrder: 'asc' });
  const { years, yearId, setYearId, effectiveYearId, defaultYear, isLoading: yearLoading, hasAnyData } =
    useDefaultYear();

  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  // Never defaults to "today" — migrated attendance sits on historical
  // dates. Seeded from the most recent date that actually has data once
  // `default-year` resolves; still freely changeable afterwards.
  const [date, setDate] = useState('');
  const [type, setType] = useState<AttendanceStatus | ''>('');

  useEffect(() => {
    if (defaultYear?.latestDate) setDate((d) => d || defaultYear.latestDate.slice(0, 10));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultYear?.latestDate]);

  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', 'all-for-selector'],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }),
  });
  const classes = classesPage?.items ?? [];
  const sections = classes.find((c) => c.id === classId)?.sections ?? [];

  const { data: pageData, isFetching } = useQuery({
    queryKey: ['attendance', 'students', dt.params, effectiveYearId, classId, sectionId, date, type],
    queryFn: () =>
      AttendanceApi.studentRoster({
        ...dt.params,
        academicYearId: effectiveYearId || undefined,
        classId: classId || undefined,
        sectionId: sectionId || undefined,
        date,
        type: type || undefined,
      }),
    enabled: !yearLoading && !!date,
    placeholderData: keepPreviousData,
  });

  const columns = [
    columnHelper.display({
      id: 'no',
      header: 'No',
      cell: (ctx) => (dt.pagination.pageIndex * dt.pagination.pageSize) + ctx.row.index + 1,
    }),
    columnHelper.accessor('admissionNo', { header: 'Admission No' }),
    columnHelper.accessor('rollNumber', {
      header: 'Roll No',
      cell: (ctx) => ctx.getValue() ?? '—',
    }),
    columnHelper.accessor('studentName', {
      header: 'Name',
      cell: (ctx) => (
        <span className="inline-flex items-center gap-2">
          {ctx.getValue()}
          {ctx.row.original.enrollmentStatus === 'left' && (
            <Badge tone="slate" className="text-[10px]">
              Left
            </Badge>
          )}
        </span>
      ),
    }),
    columnHelper.accessor('status', {
      header: 'Type',
      cell: (ctx) => {
        const s = ctx.getValue();
        return s ? (
          <Badge tone={STATUS_TONE[s]}>{STATUS_LABEL[s]}</Badge>
        ) : (
          <span className="text-xs uppercase tracking-wider text-amber-600">Not marked</span>
        );
      },
    }),
  ];

  return (
    <>
      <PageHeader
        title="View Attendance"
        description={`Browse recorded ${term.group.toLowerCase()} attendance for any date.`}
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={dt.searchInput}
            onChange={(e) => dt.setSearchInput(e.target.value)}
            placeholder="Search by name or admission #"
            className="pl-9"
          />
        </div>
        <Select className="!w-44" value={yearId || effectiveYearId} onChange={(e) => setYearId(e.target.value)}>
          {years.map((y) => (
            <option key={y.id} value={y.id}>
              {y.name}
            </option>
          ))}
        </Select>
        <Select
          className="!w-40"
          value={classId}
          onChange={(e) => {
            setClassId(e.target.value);
            setSectionId('');
          }}
        >
          <option value="">All {term.level}</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <Select
          className="!w-40"
          value={sectionId}
          onChange={(e) => setSectionId(e.target.value)}
          disabled={!classId}
        >
          <option value="">All {term.group}</option>
          {sections.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Input type="date" className="!w-40" value={date} onChange={(e) => setDate(e.target.value)} />
        <Select className="!w-36" value={type} onChange={(e) => setType(e.target.value as AttendanceStatus | '')}>
          <option value="">All types</option>
          {(Object.keys(STATUS_LABEL) as AttendanceStatus[]).map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </Select>
      </div>

      {!hasAnyData && !yearLoading ? (
        <div className="card p-8 text-center text-slate-500">
          No attendance recorded for the selected filters.
        </div>
      ) : (
        <DataTable
          columns={columns}
          result={pageData}
          state={dt}
          loading={isFetching}
          emptyMessage="No attendance recorded for the selected filters."
          getRowId={(row) => row.studentId}
        />
      )}
    </>
  );
}
